import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';
import './InlineInvoiceDemo.css';

let nextId = 3;

export default function InlineInvoiceDemo() {
    const { t, toLocalizedPath, locale } = useLocale();
    const { formatCurrency } = useCurrency();

    const [items, setItems] = useState([
        { id: 1, name: t('demo.defaultItem1Name'), qty: 1, price: parseFloat(t('demo.defaultItem1Price').replace(',', '.')) || 450 },
        { id: 2, name: t('demo.defaultItem2Name'), qty: 1, price: parseFloat(t('demo.defaultItem2Price').replace(',', '.')) || 280 },
    ]);

    function updateItem(id, field, value) {
        setItems(prev =>
            prev.map(item =>
                item.id === id ? { ...item, [field]: value } : item
            )
        );
    }

    function addItem() {
        setItems(prev => [...prev, { id: nextId++, name: '', qty: 1, price: 0 }]);
    }

    function removeItem(id) {
        if (items.length <= 1) return;
        setItems(prev => prev.filter(item => item.id !== id));
    }

    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (parseInt(item.qty, 10) || 0), 0);
    const tax = subtotal * 0.1;
    const total = subtotal + tax;

    const dateLocaleMap = { en: 'en-US', de: 'de-DE', es: 'es-ES', fr: 'fr-FR', it: 'it-IT', nl: 'nl-NL' };
    const today = new Date().toLocaleDateString(dateLocaleMap[locale] || 'en-US', { day: '2-digit', month: 'short', year: 'numeric' });

    return (
        <section id="see-it-in-action" className="demo-section sp-90">
            <div className="container">
                <h2 className="h-1 h-bold demo-heading">{t('demo.heading')}</h2>
                <p className="paragraph demo-subheading">{t('demo.subheading')}</p>

                <div className="demo-container">
                    {/* Left: Form */}
                    <div className="demo-form-side">
                        {items.map((item, index) => (
                            <div className="demo-item-row" key={item.id}>
                                <div className="demo-item-field">
                                    {index === 0 && <label>{t('demo.itemNameLabel')}</label>}
                                    <input
                                        type="text"
                                        placeholder={t('demo.itemNamePlaceholder')}
                                        value={item.name}
                                        onChange={e => updateItem(item.id, 'name', e.target.value)}
                                    />
                                </div>
                                <div className="demo-item-field-small">
                                    {index === 0 && <label>{t('demo.qtyLabel')}</label>}
                                    <input
                                        type="number"
                                        min="1"
                                        value={item.qty}
                                        onChange={e => updateItem(item.id, 'qty', e.target.value)}
                                    />
                                </div>
                                <div className="demo-item-field-small">
                                    {index === 0 && <label>{t('demo.priceLabel')}</label>}
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.price}
                                        onChange={e => updateItem(item.id, 'price', e.target.value)}
                                    />
                                </div>
                                {items.length > 1 && (
                                    <button
                                        className="demo-remove-btn"
                                        onClick={() => removeItem(item.id)}
                                        aria-label="Remove item"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        ))}
                        <button className="demo-add-btn" onClick={addItem}>
                            {t('demo.addItem')}
                        </button>
                    </div>

                    {/* Right: Live Preview */}
                    <div className="demo-preview-side">
                        <div className="demo-invoice-card">
                            <div className="demo-invoice-header">
                                <span className="demo-invoice-title">{t('demo.previewTitle')}</span>
                                <div className="demo-invoice-meta">
                                    <p>{t('demo.previewDate')}: {today}</p>
                                    <p>#INV-0042</p>
                                </div>
                            </div>

                            <div className="demo-invoice-parties">
                                <div className="demo-invoice-party">
                                    <p className="demo-invoice-party-label">{t('demo.previewFromLabel')}</p>
                                    <p className="demo-invoice-party-name">{t('demo.previewFrom')}</p>
                                </div>
                                <div className="demo-invoice-party">
                                    <p className="demo-invoice-party-label">{t('demo.previewToLabel')}</p>
                                    <p className="demo-invoice-party-name">{t('demo.previewTo')}</p>
                                </div>
                            </div>

                            <table className="demo-invoice-table">
                                <thead>
                                    <tr>
                                        <th>{t('demo.previewDescription')}</th>
                                        <th className="text-center">{t('demo.previewQty')}</th>
                                        <th className="text-right">{t('demo.previewAmount')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(item => (
                                        <tr key={item.id}>
                                            <td>{item.name || '—'}</td>
                                            <td className="text-center">{item.qty || 0}</td>
                                            <td className="text-right">
                                                {formatCurrency((parseFloat(item.price) || 0) * (parseInt(item.qty, 10) || 0))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className="demo-invoice-totals">
                                <div className="demo-invoice-total-row">
                                    <span>{t('demo.previewSubtotal')}</span>
                                    <span>{formatCurrency(subtotal)}</span>
                                </div>
                                <div className="demo-invoice-total-row">
                                    <span>{t('demo.previewTax')}</span>
                                    <span>{formatCurrency(tax)}</span>
                                </div>
                                <div className="demo-invoice-total-row total">
                                    <span>{t('demo.previewTotal')}</span>
                                    <span>{formatCurrency(total)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="demo-cta-block">
                    <Link to={toLocalizedPath('invoiceGenerator')} className="demo-cta-btn">
                        {t('demo.cta')}
                    </Link>
                    <p className="demo-cta-subtext">{t('demo.ctaSubtext')}</p>
                </div>
            </div>
        </section>
    );
}
